import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { FiArrowLeft, FiCamera } from 'react-icons/fi';
import Header from '../components/Header';
import Sidebar from '../components/Sidebar';
import LoadingSpinner from '../components/LoadingSpinner';
import { immobilisationsAPI, categoriesAPI, servicesAPI } from '../services/api';
import { toast } from 'react-toastify';

const ImmobilisationFormPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditMode = !!id;

  const [formData, setFormData] = useState({
    nom: '',
    description: '',
    categorie: '',
    service: '',
    etat: 'Bon',
    responsable: '',
    marque: '',
    modele: '',
    numSerie: '',
    photo: null,
    latitude: null,
    longitude: null,
  });

  const [categories, setCategories] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(isEditMode);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    loadSelects();
    if (isEditMode) {
      loadDetail();
    }
  }, []);

  const loadSelects = async () => {
    try {
      const [catRes, servRes] = await Promise.all([
        categoriesAPI.getAll(),
        servicesAPI.getAll(),
      ]);
      setCategories(catRes.data);
      setServices(servRes.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des données');
    }
  };

  const loadDetail = async () => {
    try {
      const res = await immobilisationsAPI.getById(id);
      setFormData({
        nom: res.data.nom || '',
        description: res.data.description || '',
        categorie: res.data.categorie?.id || '',
        service: res.data.service?.id || '',
        etat: res.data.etat || 'Bon',
        responsable: res.data.responsable || '',
        marque: res.data.marque || '',
        modele: res.data.modele || '',
        numSerie: res.data.numSerie || '',
        photo: null,
        latitude: res.data.latitude || null,
        longitude: res.data.longitude || null,
      });
    } catch (error) {
      toast.error('Erreur lors du chargement');
      navigate('/immobilisations');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData((prev) => ({ ...prev, photo: file }));
    }
  };

  const getLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setFormData((prev) => ({
            ...prev,
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
          }));
          toast.success('Position GPS capturée');
        },
        () => {
          toast.error('Impossible d\'accéder au GPS');
        }
      );
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const data = new FormData();
      Object.entries(formData).forEach(([key, value]) => {
        if (value !== null) {
          data.append(key, value);
        }
      });

      if (isEditMode) {
        await immobilisationsAPI.update(id, data);
        toast.success('Immobilisation mise à jour');
      } else {
        await immobilisationsAPI.create(data);
        toast.success('Immobilisation créée');
      }

      navigate('/immobilisations');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Erreur lors de la sauvegarde');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="app-layout">
        <Sidebar />
        <div className="main-content">
          <Header />
          <div className="page-content">
            <LoadingSpinner />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="app-layout">
      <Sidebar />
      <div className="main-content">
        <Header />
        <div className="page-content">
          <div className="page-header">
            <Link to="/immobilisations" className="btn btn-ghost">
              <FiArrowLeft /> Retour
            </Link>
            <h1 className="page-title">
              {isEditMode ? 'Éditer l\'immobilisation' : 'Nouvelle immobilisation'}
            </h1>
          </div>

          <div className="card" style={{ maxWidth: '800px' }}>
            <form onSubmit={handleSubmit}>
              {/* Photo */}
              <div className="form-group">
                <label className="form-label">Photo</label>
                <div
                  style={{
                    border: '2px dashed var(--border-color)',
                    borderRadius: 'var(--radius-lg)',
                    padding: '2rem',
                    textAlign: 'center',
                    cursor: 'pointer',
                    transition: 'var(--transition)',
                  }}
                  onClick={() => document.getElementById('photo-input').click()}
                  onDragOver={(e) => {
                    e.preventDefault();
                    e.currentTarget.style.backgroundColor = 'var(--bg-light)';
                  }}
                  onDragLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }}
                  onDrop={(e) => {
                    e.preventDefault();
                    const file = e.dataTransfer.files[0];
                    setFormData((prev) => ({ ...prev, photo: file }));
                  }}
                >
                  <FiCamera style={{ fontSize: '2rem', marginBottom: '0.5rem' }} />
                  <p>Cliquez ou glissez-déposez une image</p>
                  <input
                    id="photo-input"
                    type="file"
                    accept="image/*"
                    onChange={handleFileChange}
                    style={{ display: 'none' }}
                  />
                </div>
              </div>

              {/* Basic Info */}
              <div className="form-group">
                <label className="form-label">Nom *</label>
                <input
                  type="text"
                  name="nom"
                  value={formData.nom}
                  onChange={handleChange}
                  required
                  placeholder="Nom de l'immobilisation"
                />
              </div>

              <div className="form-group">
                <label className="form-label">Description</label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  placeholder="Description détaillée"
                ></textarea>
              </div>

              {/* Category & Service */}
              <div className="grid grid-cols-2" style={{ marginBottom: '1.5rem' }}>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label className="form-label">Catégorie *</label>
                  <select
                    name="categorie"
                    value={formData.categorie}
                    onChange={handleChange}
                    required
                  >
                    <option value="">Sélectionner une catégorie</option>
                    {categories.map((cat) => (
                      <option key={cat.id} value={cat.id}>
                        {cat.nom}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label className="form-label">Service *</label>
                  <select
                    name="service"
                    value={formData.service}
                    onChange={handleChange}
                    required
                  >
                    <option value="">Sélectionner un service</option>
                    {services.map((srv) => (
                      <option key={srv.id} value={srv.id}>
                        {srv.nom}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* State & Responsible */}
              <div className="grid grid-cols-2" style={{ marginBottom: '1.5rem' }}>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label className="form-label">État *</label>
                  <select
                    name="etat"
                    value={formData.etat}
                    onChange={handleChange}
                    required
                  >
                    <option value="Bon">Bon</option>
                    <option value="Moyen">Moyen</option>
                    <option value="Hors service">Hors service</option>
                  </select>
                </div>

                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label className="form-label">Responsable</label>
                  <input
                    type="text"
                    name="responsable"
                    value={formData.responsable}
                    onChange={handleChange}
                    placeholder="Nom du responsable"
                  />
                </div>
              </div>

              {/* Technical Info */}
              <div className="grid grid-cols-3" style={{ marginBottom: '1.5rem' }}>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label className="form-label">Marque</label>
                  <input
                    type="text"
                    name="marque"
                    value={formData.marque}
                    onChange={handleChange}
                    placeholder="Marque"
                  />
                </div>

                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label className="form-label">Modèle</label>
                  <input
                    type="text"
                    name="modele"
                    value={formData.modele}
                    onChange={handleChange}
                    placeholder="Modèle"
                  />
                </div>

                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label className="form-label">N° de Série</label>
                  <input
                    type="text"
                    name="numSerie"
                    value={formData.numSerie}
                    onChange={handleChange}
                    placeholder="Numéro de série"
                  />
                </div>
              </div>

              {/* GPS */}
              <div className="form-group">
                <label className="form-label">Localisation GPS</label>
                <div
                  style={{
                    display: 'flex',
                    gap: '1rem',
                    padding: '1rem',
                    backgroundColor: 'var(--bg-light)',
                    borderRadius: 'var(--radius-md)',
                  }}
                >
                  <div style={{ flex: 1 }}>
                    <label style={{ fontSize: '0.875rem', color: 'var(--text-light)' }}>
                      Latitude
                    </label>
                    <input
                      type="number"
                      step="0.0001"
                      value={formData.latitude || ''}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          latitude: e.target.value ? parseFloat(e.target.value) : null,
                        }))
                      }
                      placeholder="Latitude"
                    />
                  </div>

                  <div style={{ flex: 1 }}>
                    <label style={{ fontSize: '0.875rem', color: 'var(--text-light)' }}>
                      Longitude
                    </label>
                    <input
                      type="number"
                      step="0.0001"
                      value={formData.longitude || ''}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          longitude: e.target.value ? parseFloat(e.target.value) : null,
                        }))
                      }
                      placeholder="Longitude"
                    />
                  </div>

                  <button
                    type="button"
                    onClick={getLocation}
                    className="btn btn-secondary"
                    style={{ alignSelf: 'flex-end' }}
                  >
                    <FiCamera /> Capturer
                  </button>
                </div>
              </div>

              {/* Actions */}
              <div style={{ display: 'flex', gap: '1rem', marginTop: '2rem' }}>
                <button
                  type="button"
                  onClick={() => navigate('/immobilisations')}
                  className="btn btn-ghost"
                  style={{ flex: 1 }}
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={submitting}
                  style={{ flex: 1 }}
                >
                  {submitting ? 'Sauvegarde...' : isEditMode ? 'Mettre à jour' : 'Créer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ImmobilisationFormPage;
